/**
 * Regular-expression evaluation for the /regex tool.
 *
 * The engine is the browser's own `RegExp`, so what the tester reports is
 * exactly what `match`, `replace` and `split` will do in the visitor's code.
 * The scanning loop is the part worth owning: a global match can return a
 * zero-length hit, which leaves `lastIndex` where it was and spins forever
 * unless it is advanced by hand, and a pathological pattern can produce more
 * matches than any list should render.
 */

/** Every flag the tester offers, with the copy the checkbox row renders. */
export const REGEX_FLAGS: Array<{ flag: string; label: string; hint: string }> = [
  { flag: 'g', label: 'global', hint: 'Find every match, not just the first' },
  { flag: 'i', label: 'ignore case', hint: 'Match regardless of letter case' },
  { flag: 'm', label: 'multiline', hint: '^ and $ match at line breaks' },
  { flag: 's', label: 'dot all', hint: '. also matches newlines' },
  { flag: 'u', label: 'unicode', hint: 'Treat the pattern as Unicode code points' },
  { flag: 'y', label: 'sticky', hint: 'Match only from lastIndex' },
];

/** Hard ceiling on reported matches; a runaway pattern must not hang the tab. */
export const MAX_MATCHES = 1000;

export interface RegexMatch {
  /** 1-based position in the result list, which is what the UI labels. */
  ordinal: number;
  index: number;
  value: string;
  /** Numbered capture groups, in order; a group that did not participate is undefined. */
  groups: Array<string | undefined>;
  namedGroups: Record<string, string | undefined>;
}

/** A run of the subject text, flagged when it belongs to a match. */
export interface RegexSegment {
  text: string;
  /** The 1-based ordinal of the match this segment is part of, or null for plain text. */
  matchOrdinal: number | null;
}

export interface RegexResult {
  matches: RegexMatch[];
  /** The subject split into matched and unmatched runs, for highlighting. */
  segments: RegexSegment[];
  /** True when the scan stopped at MAX_MATCHES. */
  truncated: boolean;
  /** Present only when a replacement template was supplied. */
  replaced?: string;
}

export class RegexError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RegexError';
  }
}

/** Compile the pattern, turning the engine's SyntaxError into user-facing copy. */
export function compileRegex(pattern: string, flags: string): RegExp {
  try {
    return new RegExp(pattern, flags);
  } catch (error) {
    throw new RegexError((error as Error).message);
  }
}

function toMatch(match: RegExpExecArray, ordinal: number): RegexMatch {
  return {
    ordinal,
    index: match.index,
    value: match[0],
    groups: match.slice(1),
    namedGroups: { ...(match.groups ?? {}) },
  };
}

/** Split the subject into alternating plain and matched runs. */
function buildSegments(input: string, matches: RegexMatch[]): RegexSegment[] {
  const segments: RegexSegment[] = [];
  let cursor = 0;

  for (const match of matches) {
    // A zero-length match has nothing to paint; skipping it also keeps the
    // cursor monotonic so the trailing slice below stays correct.
    if (match.value.length === 0) {
      continue;
    }
    if (match.index > cursor) {
      segments.push({ text: input.slice(cursor, match.index), matchOrdinal: null });
    }
    segments.push({ text: match.value, matchOrdinal: match.ordinal });
    cursor = match.index + match.value.length;
  }

  if (cursor < input.length) {
    segments.push({ text: input.slice(cursor), matchOrdinal: null });
  }
  return segments;
}

/**
 * Run `pattern` over `input`.
 *
 * Without the `g` flag only the first match is reported, matching the engine.
 * `replacement` is optional; when given, its result is computed with the same
 * regex so `$1` and `$<name>` behave exactly as they would in the caller's code.
 */
export function runRegex(
  pattern: string,
  flags: string,
  input: string,
  replacement?: string,
): RegexResult {
  const regex = compileRegex(pattern, flags);
  const matches: RegexMatch[] = [];
  let truncated = false;

  if (!pattern || !input) {
    return { matches, segments: input ? [{ text: input, matchOrdinal: null }] : [], truncated };
  }

  if (regex.global || regex.sticky) {
    regex.lastIndex = 0;
    let current = regex.exec(input);
    while (current !== null) {
      matches.push(toMatch(current, matches.length + 1));
      if (matches.length >= MAX_MATCHES) {
        truncated = true;
        break;
      }
      // An empty match leaves lastIndex untouched; step over it by hand.
      if (current[0] === '') {
        regex.lastIndex += 1;
      }
      current = regex.exec(input);
    }
  } else {
    const single = regex.exec(input);
    if (single) {
      matches.push(toMatch(single, 1));
    }
  }

  const result: RegexResult = {
    matches,
    segments: buildSegments(input, matches),
    truncated,
  };

  if (replacement !== undefined) {
    // A fresh regex: exec() above consumed lastIndex on the sticky/global one.
    result.replaced = input.replace(compileRegex(pattern, flags), replacement);
  }

  return result;
}
